import React, { useState, useEffect } from 'react';
import './DevicePage.css';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { FiPlusCircle } from 'react-icons/fi';
import { buildApiUrl, getAuthHeaders } from '../../config/api';
import { useRestaurant } from '../../context/RestaurantContext';
import { notify, confirm } from '../../context/FeedbackContext';
import { getFilteredNavItemsWithIcons, getRouteFromItem } from '../../config/routes';
import HasPermi from '../../components/HasPermi';

const EMPTY_FORM = {
  deviceId: '',
  deviceName: '',
  deviceType: 'kiosk',
  model: '',
  ip: '',
  status: '1',
};

const DEVICE_TYPES = [
  { value: 'kiosk', label: 'Kiosk' },
  { value: 'pos', label: 'POS' },
  { value: 'tablet', label: 'Tablet' },
];

function DevicePage({ onNavigate }) {
  const { selectedRestaurant } = useRestaurant() || {};
  const selectedStoreRef = selectedRestaurant?.reference || '';

  const [devices, setDevices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [editForm, setEditForm] = useState({});
  const [addFormErrors, setAddFormErrors] = useState({});
  const [editFormErrors, setEditFormErrors] = useState({});
  const [goToPage, setGoToPage] = useState('');

  const itemsPerPage = 10;

  const handleNavClick = (item) => {
    if (onNavigate) onNavigate(getRouteFromItem(item));
  };

  // ── Fetch devices ──
  useEffect(() => {
    if (!selectedStoreRef) return;
    const timer = setTimeout(() => {
      fetchDevices();
    }, searchTerm ? 400 : 0);
    return () => clearTimeout(timer);
  }, [selectedStoreRef, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDevices = async () => {
    setIsLoading(true);
    try {
      const url = searchTerm
        ? buildApiUrl(`/tenant/device/by-tenant?tenantReference=${selectedStoreRef}&searchTerm=${encodeURIComponent(searchTerm)}`)
        : buildApiUrl(`/tenant/device/by-tenant?tenantReference=${selectedStoreRef}`);
      const res = await fetch(url, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.code === 200) {
        setDevices(data.data || []);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error('Failed to fetch devices', err);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Pagination ──
  const totalPages = Math.ceil(devices.length / itemsPerPage);
  const paginatedDevices = devices.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const showingFrom = devices.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const showingTo = Math.min(currentPage * itemsPerPage, devices.length);

  // ── Add ──
  const handleAddInputChange = (e) => {
    const { name, value } = e.target;
    setAddForm(prev => ({ ...prev, [name]: value }));
    setAddFormErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (form) => {
    const errors = {};
    if (!form.deviceId?.trim()) errors.deviceId = 'Device ID is required.';
    if (!form.deviceName?.trim()) errors.deviceName = 'Device Name is required.';
    if (form.ip && !/^[\d.:]+$/.test(form.ip.trim())) errors.ip = 'Please enter a valid IP address.';
    return errors;
  };

  const handleAddDevice = async () => {
    const errors = validateForm(addForm);
    setAddFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (!selectedStoreRef) {
      notify('No store selected. Cannot add device.', 'error');
      return;
    }

    try {
      const res = await fetch(buildApiUrl(`/tenant/device/create`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          tenantReference: selectedStoreRef,
          deviceId: addForm.deviceId.trim(),
          deviceName: addForm.deviceName.trim(),
          deviceType: addForm.deviceType,
          model: addForm.model.trim(),
          ip: addForm.ip.trim(),
          status: Number(addForm.status),
        }),
      });
      const data = await res.json();
      if (data.code === 200) {
        setShowAddModal(false);
        setAddForm(EMPTY_FORM);
        fetchDevices();
      } else {
        notify('Failed to add device: ' + (data.msg || 'Unknown error'), 'error');
      }
    } catch (err) {
      notify('Failed to add device: ' + err.message, 'error');
    }
  };

  // ── Edit ──
  const handleEdit = (device) => {
    setEditForm({
      reference: device.reference,
      deviceId: device.deviceId || '',
      deviceName: device.deviceName || '',
      deviceType: device.deviceType || 'kiosk',
      model: device.model || '',
      ip: device.ip || '',
      status: String(device.status ?? 1),
    });
    setEditFormErrors({});
    setShowEditModal(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
    setEditFormErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSaveEdit = async () => {
    const errors = validateForm(editForm);
    setEditFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const res = await fetch(buildApiUrl(`/tenant/device/update`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          reference: editForm.reference,
          deviceName: editForm.deviceName.trim(),
          deviceType: editForm.deviceType,
          model: editForm.model.trim(),
          ip: editForm.ip.trim(),
          status: Number(editForm.status),
        }),
      });
      const data = await res.json();
      if (data.code === 200) {
        setShowEditModal(false);
        fetchDevices();
      } else {
        notify('Failed to update device: ' + (data.msg || 'Unknown error'), 'error');
      }
    } catch (err) {
      notify('Failed to update device: ' + err.message, 'error');
    }
  };

  // ── Delete ──
  const handleDelete = async (device) => {
    if (await confirm(`Are you sure you want to delete device "${device.deviceName}"?`)) {
      try {
        const res = await fetch(buildApiUrl(`/tenant/device/delete`), {
          method: 'POST',
          body: JSON.stringify({ reference: device.reference }),
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        });
        const data = await res.json();
        if (data.code === 200) {
          fetchDevices();
        } else {
          notify('Failed to delete device: ' + (data.msg || 'Unknown error'), 'error');
        }
      } catch (err) {
        notify('Failed to delete device: ' + err.message, 'error');
      }
    }
  };

  // ── Helpers ──
  const getDeviceTypeBadge = (type) => {
    const map = { kiosk: 'type-kiosk', pos: 'type-pos', tablet: 'type-tablet' };
    return map[type] || 'type-default';
  };

  // ── Pagination buttons ──
  const renderPageButtons = () => {
    const buttons = [];
    buttons.push(
      <button key="prev" className={`pg-btn${currentPage === 1 ? ' disabled' : ''}`}
        onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}>Previous</button>
    );
    const start = Math.max(1, Math.min(currentPage - 1, totalPages - 2));
    const end = Math.min(totalPages, start + 2);
    buttons.push(<span key="dots-start" className="pg-dots" style={{ visibility: start > 1 ? 'visible' : 'hidden' }}>...</span>);
    for (let i = start; i <= end; i++) {
      buttons.push(
        <button key={i} className={`pg-btn${currentPage === i ? ' active' : ''}`}
          onClick={() => setCurrentPage(i)}>{i}</button>
      );
    }
    buttons.push(<span key="dots-end" className="pg-dots" style={{ visibility: end < totalPages ? 'visible' : 'hidden' }}>...</span>);
    buttons.push(
      <button key="next" className={`pg-btn${currentPage === totalPages ? ' disabled' : ''}`}
        onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}>Next</button>
    );
    return buttons;
  };

  const { items: navItems, icons: navIcons } = getFilteredNavItemsWithIcons();

  return (
    <div className="devicepage-container">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item => (
            <li key={item} className={item === 'Device' ? 'active' : ''}
              onClick={() => handleNavClick(item)}>
              <span className="nav-icon">{navIcons[item]}</span> {item}
            </li>
          ))}
        </ul>
      </nav>

      <main className="device-content">
        <div className="admin-breadcrumb">
          <span className="breadcrumb-link" onClick={() => handleNavClick('Home')}>Home</span>
          <span className="breadcrumb-sep">&rsaquo;</span>
          <span className="breadcrumb-current">Device Management</span>
        </div>
        <h1 className="admin-page-title">Device Management</h1>
        <p className="admin-page-subtitle">Manage devices — create, edit, enable/disable and remove.</p>

        {/* Table */}
        <HasPermi permission={['device:list', 'device:search']} any>
          <div className="device-table-card">
            <div className="device-table-header">
              <h2 className="device-table-title">Device List</h2>
              <div className="device-table-actions">
                <div className="search-box">
                  <input type="text" placeholder="Search by name or device ID..."
                    value={searchTerm}
                    onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
                </div>
                <HasPermi permission="device:create">
                  <button className="add-device-btn"
                    onClick={() => { setShowAddModal(true); setAddForm(EMPTY_FORM); setAddFormErrors({}); }}>
                    <FiPlusCircle /> Add Device
                  </button>
                </HasPermi>
              </div>
            </div>

            <table className="device-table">
              <thead>
                <tr>
                  <th>DEVICE ID</th>
                  <th>NAME</th>
                  <th>TYPE</th>
                  <th>MODEL</th>
                  <th>IP</th>
                  <th>STATUS</th>
                  <th>CREATED AT</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>Loading...</td></tr>
                ) : paginatedDevices.length === 0 ? (
                  <tr><td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>No devices found.</td></tr>
                ) : paginatedDevices.map(device => (
                  <tr key={device.id}>
                    <td><code>{device.deviceId}</code></td>
                    <td>{device.deviceName}</td>
                    <td>
                      <span className={`device-type-badge ${getDeviceTypeBadge(device.deviceType)}`}>
                        {device.deviceType}
                      </span>
                    </td>
                    <td>{device.model || '-'}</td>
                    <td>{device.ip || '-'}</td>
                    <td>
                      <span className={`status-badge ${device.status === 1 ? 'enabled' : 'disabled'}`}>
                        {device.status === 1 ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td>{device.createdAt}</td>
                    <td className="actions">
                      <HasPermi permission="device:update">
                        <button className="edit-btn" onClick={() => handleEdit(device)}><FaEdit /></button>
                      </HasPermi>
                      <HasPermi permission="device:delete">
                        <button className="delete-btn" onClick={() => handleDelete(device)}><FaTrash /></button>
                      </HasPermi>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="pagination-row">
              <span className="pagination-info">Showing {showingFrom} to {showingTo} of {devices.length} results</span>
              <div className="pagination-buttons">{renderPageButtons()}</div>
              <div className="pagination-goto">
                <span>Go to page</span>
                <input className="pg-goto-input" type="number" min="1" max={totalPages} value={goToPage}
                  onChange={e => setGoToPage(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { const p = parseInt(goToPage); if (p >= 1 && p <= totalPages) { setCurrentPage(p); setGoToPage(''); } } }}
                  placeholder="" />
                <button className="pg-btn" onClick={() => { const p = parseInt(goToPage); if (p >= 1 && p <= totalPages) { setCurrentPage(p); setGoToPage(''); } }}>Go</button>
              </div>
            </div>
          </div>
        </HasPermi>

        <HasPermi
          permission={['device:list', 'device:search']}
          any
          fallback={<div className="no-permission-message"><p>You don't have permission to view devices.</p></div>}
        />
      </main>

      {/* Add Modal */}
      <HasPermi permission="device:create">
        {showAddModal && (
          <div className="admin-modal-overlay">
            <div className="admin-modal" onClick={e => e.stopPropagation()}>
              <div className="admin-modal-header">
                <button className="admin-modal-close" onClick={() => setShowAddModal(false)}>&times;</button>
                <h2 className="admin-modal-title">Add Device</h2>
                <p className="admin-modal-subtitle">Register a new device. Fill in all required fields.</p>
              </div>
              <div className="admin-modal-body">
                <div className="form-field">
                  <label>Device ID <span className="required">*</span></label>
                  <input className={addFormErrors.deviceId ? 'input-error' : ''} name="deviceId" value={addForm.deviceId}
                    onChange={handleAddInputChange} placeholder="e.g. KIOSK-001" />
                  {addFormErrors.deviceId && <p className="field-error">{addFormErrors.deviceId}</p>}
                </div>
                <div className="form-field">
                  <label>Device Name <span className="required">*</span></label>
                  <input className={addFormErrors.deviceName ? 'input-error' : ''} name="deviceName" value={addForm.deviceName}
                    onChange={handleAddInputChange} placeholder="e.g. 大堂自助点餐机" />
                  {addFormErrors.deviceName && <p className="field-error">{addFormErrors.deviceName}</p>}
                </div>
                <div className="form-field">
                  <label>Device Type</label>
                  <select name="deviceType" value={addForm.deviceType} onChange={handleAddInputChange}>
                    {DEVICE_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Model</label>
                  <input name="model" value={addForm.model}
                    onChange={handleAddInputChange} placeholder="e.g. A1 Pro" />
                </div>
                <div className="form-field">
                  <label>IP Address</label>
                  <input className={addFormErrors.ip ? 'input-error' : ''} name="ip" value={addForm.ip}
                    onChange={handleAddInputChange} placeholder="e.g. 192.168.1.100" />
                  {addFormErrors.ip && <p className="field-error">{addFormErrors.ip}</p>}
                </div>
                <div className="form-field">
                  <label>Status</label>
                  <select name="status" value={addForm.status} onChange={handleAddInputChange}>
                    <option value="1">Enabled</option>
                    <option value="0">Disabled</option>
                  </select>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button className="cancel-btn" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button className="submit-btn" onClick={handleAddDevice}>Add Device</button>
              </div>
            </div>
          </div>
        )}
      </HasPermi>

      {/* Edit Modal */}
      <HasPermi permission="device:update">
        {showEditModal && (
          <div className="admin-modal-overlay">
            <div className="admin-modal" onClick={e => e.stopPropagation()}>
              <div className="admin-modal-header">
                <button className="admin-modal-close" onClick={() => setShowEditModal(false)}>&times;</button>
                <h2 className="admin-modal-title">Edit Device</h2>
                <p className="admin-modal-subtitle">Update the device information.</p>
              </div>
              <div className="admin-modal-body">
                <div className="form-field">
                  <label>Device ID</label>
                  <input value={editForm.deviceId || ''} disabled className="disabled-input" />
                </div>
                <div className="form-field">
                  <label>Device Name <span className="required">*</span></label>
                  <input className={editFormErrors.deviceName ? 'input-error' : ''} name="deviceName" value={editForm.deviceName || ''}
                    onChange={handleEditInputChange} placeholder="e.g. 大堂自助点餐机" />
                  {editFormErrors.deviceName && <p className="field-error">{editFormErrors.deviceName}</p>}
                </div>
                <div className="form-field">
                  <label>Device Type</label>
                  <select name="deviceType" value={editForm.deviceType || 'kiosk'} onChange={handleEditInputChange}>
                    {DEVICE_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Model</label>
                  <input name="model" value={editForm.model || ''}
                    onChange={handleEditInputChange} placeholder="e.g. A1 Pro" />
                </div>
                <div className="form-field">
                  <label>IP Address</label>
                  <input className={editFormErrors.ip ? 'input-error' : ''} name="ip" value={editForm.ip || ''}
                    onChange={handleEditInputChange} placeholder="e.g. 192.168.1.100" />
                  {editFormErrors.ip && <p className="field-error">{editFormErrors.ip}</p>}
                </div>
                <div className="form-field">
                  <label>Status</label>
                  <select name="status" value={editForm.status ?? '1'} onChange={handleEditInputChange}>
                    <option value="1">Enabled</option>
                    <option value="0">Disabled</option>
                  </select>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button className="cancel-btn" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button className="submit-btn" onClick={handleSaveEdit}>Save Changes</button>
              </div>
            </div>
          </div>
        )}
      </HasPermi>
    </div>
  );
}

export default DevicePage;